import React, { useEffect, useState, useRef } from 'react'
import Logo from '../../../images/public/websitefooter.png'
import { useSearchParams } from 'react-router-dom';
import Functions from './function';
import { palmerDesc, palmerAsc, unitCount } from '../../../configTools/toothNumbering'
import { formatDate, monthName } from '../../../configTools/dateFormat'
import { formatMoney } from '../../../configTools/currency'
import './index.css'

// The labs table stores only name and contact, so these have no backend source and
// stay hardcoded - kept here rather than buried in the markup.
const LAB = {
    name: 'N-Vision Digital Dental Laboratory',
    // Shown in brackets after the name in the "Bill from" block only. Deliberately a separate
    // field rather than folded into `name`, which also feeds the logo alt text and the
    // "Thank you for choosing ..." sentence in the notes - a bracketed personal name would
    // read oddly in both.
    person: 'Adil Akram',
    address: 'P-369 Block B, Street #2, Alnajaf Colony, Faisalabad, Pakistan 38000',
    contact: '+92 (300) 460 - 1314',
    email: 'fsd@nvisiondental.com'
};

const TEETH_COLUMNS = 7;
const TOOTH_SEP = ' ';

const Index = () => {
    const [searchParams] = useSearchParams();
    const [billingdata,setBillingData]= useState(null);
    const inprint = useRef(false);
    useEffect(()=>{
        var doctor = searchParams.get('doctor') || "0";
        var month = searchParams.get('month') || "0";
        var duedateparam = searchParams.get('duedate') || "";
        Functions.Get(doctor,month,duedateparam,(Data)=>{
            setBillingData(Data);
            if(!inprint.current){
                setTimeout(() => {
                    window.print();
                    //window.close();
                }, 400);
            }
            inprint.current = true;
        });
    },[searchParams])

    const duedate = formatDate(searchParams.get('duedate'));
    // Carried on the URL by both callers that open this page - the billing list's reprint
    // button and the print-after-create flow. Absent on an old bookmarked URL, so the render
    // is guarded rather than printing a bare "BL".
    const billno = searchParams.get('billno') || '';
    // The period year comes from the bill (the API derives it from month + due date), not
    // from the browser clock - otherwise reprinting last year's statement mislabels it.
    // The clock is only a fallback for a frontend deployed ahead of the API.
    const period = billingdata
        ? `${monthName(searchParams.get('month'))} ${billingdata.year || new Date().getFullYear()}`
        : '';

    // Space-separated, not comma-separated: a full quadrant ("8 7 6 5 4 3 2 1") has to fit on
    // one line in this column, and dropping the commas saves ~14pt of the ~50pt needed.
    // Only this page passes a separator - the job cards keep their commas.
    const teeth = (sections) => (
        <div className='nv-stmt__teeth'>
            <span>{palmerDesc(sections.S1, TOOTH_SEP)}</span>
            <span>{palmerAsc(sections.S2, TOOTH_SEP)}</span>
            <span>{palmerDesc(sections.S3, TOOTH_SEP)}</span>
            <span>{palmerAsc(sections.S4, TOOTH_SEP)}</span>
        </div>
    );

  return (
    <div className='nv-stmt'>
    {billingdata &&
        <>
        <div className='nv-stmt__head'>
            <img className='nv-stmt__logo' src={Logo} alt={LAB.name}/>
            <div className='nv-stmt__head-right'>
                <h1 className='nv-stmt__title'>Monthly Statement</h1>
                <p className='nv-stmt__period'>{period}</p>
                {billno &&
                    <p className='nv-stmt__billno'>Bill No <b>BL{billno}</b></p>
                }
            </div>
        </div>

        <div className='nv-stmt__parties'>
            <div className='nv-stmt__party'>
                <p className='nv-stmt__eyebrow'>Bill to</p>
                <p className='nv-stmt__party-name'>{billingdata.clinicname}</p>
                <p className='nv-stmt__party-line'>
                    {billingdata.doctorname}<br/>
                    {billingdata.address}<br/>
                    {billingdata.contact} &middot; {billingdata.email}
                </p>
            </div>
            <div className='nv-stmt__party'>
                <p className='nv-stmt__eyebrow'>Bill from</p>
                <p className='nv-stmt__party-name'>{LAB.name} ({LAB.person})</p>
                <p className='nv-stmt__party-line'>
                    {LAB.address}<br/>
                    {LAB.contact} &middot; {LAB.email}
                </p>
            </div>
            <div className='nv-stmt__due'>
                <p className='nv-stmt__eyebrow'>Amount due</p>
                <p className='nv-stmt__due-value'>PKR {formatMoney(billingdata.payables)}</p>
                <div className='nv-stmt__due-by'>
                    <p className='nv-stmt__eyebrow'>Pay before</p>
                    <p className='nv-stmt__due-by-value'>{duedate}</p>
                </div>
            </div>
        </div>

        <div className='nv-stmt__section'>
            <h2 className='nv-stmt__section-title'>Work supplied</h2>
            <table className='nv-stmt__table'>
                <thead>
                    <tr>
                        <th>Invoice</th>
                        <th>Date</th>
                        <th>Patient</th>
                        <th>Work type</th>
                        <th className='-center'>Tooth</th>
                        <th className='-num'>Units</th>
                        <th className='-num'>Amount</th>
                    </tr>
                </thead>
                <tbody>
                {billingdata.orders.length === 0 &&
                    <tr>
                        <td className='nv-stmt__empty' colSpan={TEETH_COLUMNS}>No work recorded for this period</td>
                    </tr>
                }
                {billingdata.orders.map((item,index)=>{
                    const items = Array.isArray(item.items) ? item.items : [];
                    // rowSpan={0} would span the rest of the table, so an order with no
                    // line items gets a single row with the work columns left blank.
                    const span = items.length || 1;
                    const lead = (
                        <>
                            <td className='-strong -group' rowSpan={span}>{item.id}</td>
                            <td className='-group' rowSpan={span}>{formatDate(item.date)}</td>
                            <td className='-group' rowSpan={span}>{item.patient}</td>
                        </>
                    );
                    const amount = <td className='-num -strong' rowSpan={span}>{formatMoney(item.subtotal)}</td>;
                    if(items.length === 0){
                        return (
                            <tr key={index}>
                                {lead}
                                <td className='-group'></td>
                                <td className='-group'></td>
                                <td className='-num -group'></td>
                                {amount}
                            </tr>
                        );
                    }
                    return items.map((lineitem,index2)=>(
                        <tr key={`${index}-${index2}`}>
                            {index2 === 0 && lead}
                            <td className='-group'>{lineitem.productname} / {lineitem.servicename}</td>
                            <td className='-group'>{teeth(lineitem.sections)}</td>
                            <td className='-num -group'>{unitCount(lineitem.sections)}</td>
                            {index2 === 0 && amount}
                        </tr>
                    ));
                })}
                </tbody>
            </table>
        </div>

        {billingdata.payments.length > 0 &&
            <div className='nv-stmt__section'>
                <h2 className='nv-stmt__section-title'>Payments received</h2>
                <table className='nv-stmt__table'>
                    <thead>
                        <tr>
                            <th>Ref</th>
                            <th>Date</th>
                            <th>Description</th>
                            <th className='-num'>Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                    {billingdata.payments.map((item,index)=>(
                        <tr key={index}>
                            <td className='-strong'>{item.id}</td>
                            <td>{formatDate(item.date)}</td>
                            <td>{item.description}</td>
                            <td className='-num'>{formatMoney(item.amount)}</td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
        }

        <div className='nv-stmt__foot'>
            <div className='nv-stmt__notes'>
                <p className='nv-stmt__eyebrow'>Terms &amp; Notices</p>
                <ul className='nv-stmt__terms'>
                    <li>
                        <b>Payment Terms:</b> Payment is due by {duedate}.
                    </li>
                    <li>
                        <b>Discrepancies:</b> If any submitted payment does not appear on this
                        statement, please notify us immediately. Payments received after the
                        statement date will be reflected on your next monthly statement.
                    </li>
                    <li>
                        <b>Appreciation:</b> Thank you for choosing {LAB.name}. We truly
                        appreciate your business.
                    </li>
                </ul>
            </div>
            <table className='nv-stmt__totals'>
                <tbody>
                    <tr>
                        <td>Previous balance</td>
                        <td>PKR {formatMoney(billingdata.remaining)}</td>
                    </tr>
                    <tr>
                        <td>Charges this month</td>
                        <td>PKR {formatMoney(billingdata.totalamount)}</td>
                    </tr>
                    <tr>
                        <td>Discount</td>
                        <td>PKR {formatMoney(billingdata.discount)}</td>
                    </tr>
                    <tr>
                        <td>Net charges</td>
                        <td>PKR {formatMoney(billingdata.totalamountafterdiscount)}</td>
                    </tr>
                    {billingdata.payments.length > 0 &&
                        <tr>
                            <td>Payments received</td>
                            <td>PKR {formatMoney(billingdata.totalpaidthismonth)}</td>
                        </tr>
                    }
                    <tr className='-due'>
                        <td>Amount due</td>
                        <td>PKR {formatMoney(billingdata.payables)}</td>
                    </tr>
                </tbody>
            </table>
        </div>

        </>
    }
    </div>
  )
}

export default Index
